/**
 * Cashflow report (Phase 3, §6.3). Grouped Einnahmen (green) / Ausgaben (red)
 * bars — both positive magnitudes — with a Netto line on the SAME single EUR
 * y-axis (never dual-axis), plus a totals/averages summary. Consumes the spine
 * `BarChart`; semantic colours only (never a categorical data series).
 */
import { money } from '../../lib/format.ts';
import { formatMonthShort } from '../../lib/format.ts';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { Skeleton } from '../../components/ui/Skeleton.tsx';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { useReportCashflow } from '../../lib/hooks/useReports.ts';
import type { MonthRange } from '../../lib/queryKeys.ts';
import { BarChart } from './charts/BarChart.tsx';
import { useChartMode } from './charts/useChartDims.tsx';
import { semanticColor } from './charts/palette.ts';

const toNum = (m: string): number => {
  const n = Number.parseFloat(m);
  return Number.isFinite(n) ? n : 0;
};

export function CashflowReport({ range }: { range: MonthRange }) {
  const mode = useChartMode();
  const { data, isLoading, isError, error, refetch } = useReportCashflow(range);

  if (isLoading) return <Skeleton className="h-72 w-full" />;
  if (isError) {
    return (
      <ErrorState title="Cashflow konnte nicht geladen werden" error={error} onRetry={refetch} />
    );
  }
  if (!data || data.months.length === 0) {
    return <EmptyState title="Keine Daten im Zeitraum" description="Für diesen Zeitraum gibt es keine gebuchten Umsätze." />;
  }

  const categories = data.months.map((m) => formatMonthShort(m.month));
  const income = data.months.map((m) => toNum(m.income));
  const expense = data.months.map((m) => toNum(m.expense));
  const net = data.months.map((m) => toNum(m.net));

  return (
    <div className="space-y-8">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
        <SummaryCell label="Einnahmen gesamt" value={data.totals.income} tone="pos" />
        <SummaryCell label="Ausgaben gesamt" value={`-${data.totals.expense}`} tone="neg" />
        <SummaryCell label="Netto" value={data.totals.net} tone="amount" />
        <div className="flex flex-col gap-1">
          <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
            Ø pro Monat
          </dt>
          <dd className="text-sm tabular-nums text-slate-600 dark:text-slate-400">
            {money(data.totals.avg_income)} / −{money(data.totals.avg_expense)}
          </dd>
        </div>
      </dl>

      <div>
        <BarChart
          title="Einnahmen und Ausgaben"
          subtitle="Monatlicher Cashflow mit Netto-Linie"
          description="Gruppierte Balken für Einnahmen und Ausgaben je Monat mit einer Netto-Linie auf derselben Euro-Achse."
          categories={categories}
          series={[
            { key: 'income', label: 'Einnahmen', color: semanticColor('income', mode), values: income },
            { key: 'expense', label: 'Ausgaben', color: semanticColor('expense', mode), values: expense },
          ]}
          netLine={{ label: 'Netto', color: semanticColor('net', mode), values: net }}
        />
      </div>
    </div>
  );
}

function SummaryCell({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'pos' | 'neg' | 'amount';
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
        {label}
      </dt>
      <dd className="text-sm tabular-nums">
        {tone === 'amount' ? (
          <AmountText value={value} />
        ) : (
          <span
            className={
              tone === 'pos'
                ? 'font-medium text-emerald-600 dark:text-emerald-400'
                : 'font-medium text-rose-600 dark:text-rose-400'
            }
          >
            {money(value)}
          </span>
        )}
      </dd>
    </div>
  );
}
